// Mirrors `@therapylake/package/ai-agent`. The client build cannot import the
// package (see utils/constants.js), so these are kept in sync by hand.
export const AI_ASSISTANT_SOCKET_EVENT = 'ai-assistant:event';

/**
 * Mirrors `AiGovernanceErrorCode` (`@therapylake/package/ai-governance`) — the `code` of the 403/429/503
 * sent when an EachBase admin turned AI or the assistant off, the user's spend limit is used up, or no
 * OpenAI API key is set for the assistant (503 `ai-not-configured`).
 * The server's `message` is user-facing (it names the feature and when the limit resets) and is shown as is.
 */
export const AiGovernanceErrorCode = {
    AI_DISABLED: 'ai-disabled',
    ASSISTANT_DISABLED: 'ai-assistant-disabled',
    LIMIT_REACHED: 'ai-limit-reached',
    NOT_CONFIGURED: 'ai-not-configured',
};

export const AgentEventType = {
    TEXT_DELTA: 'text.delta',
    TOOL_STARTED: 'tool.started',
    TOOL_FINISHED: 'tool.finished',
    TURN_COMPLETED: 'turn.completed',
    TURN_FAILED: 'turn.failed',
};

export const AgentErrorCode = {
    SESSION_BUSY: 'session-busy',
    SESSION_FULL: 'session-full',
    INVALID_REQUEST: 'invalid-request',
    MODEL_FAILED: 'model-failed',
    REPLY_INCOMPLETE: 'reply-incomplete',
    TURN_EXPIRED: 'turn-expired',
    /** No OpenAI API key is set for the assistant — the turn was not run. */
    NOT_CONFIGURED: 'not-configured',
};

export const MESSAGE_MAX_LENGTH = 8000;

export const MessageRole = {
    USER: 'user',
    ASSISTANT: 'assistant',
    /** A line from the app itself, not the model (e.g. "earlier messages are no longer remembered"). */
    NOTICE: 'notice',
};

/** What a running reply is doing besides streaming text. A tool's name means it is looking something up. */
export const TurnActivity = {
    RECONNECTING: 'reconnecting',
    WAITING: 'waiting',
};

export const MessageStatus = {
    STREAMING: 'streaming',
    DONE: 'done',
    ERROR: 'error',
};

/** No event for this long means the turn is lost (worker down, socket dropped). */
export const TURN_STALL_TIMEOUT_MS = 150000;

/**
 * The server forgets a session after 1h idle (`AI_AGENT_SESSION_TTL_SEC`). A little earlier than that, the
 * next message starts a new session and says so, instead of the model silently losing the conversation
 * the user still sees.
 */
export const SESSION_IDLE_MS = 55 * 60 * 1000;

/**
 * After the socket reconnects mid-reply, wait this long for the reply to continue. Events sent while
 * offline are lost — if the turn already ended, nothing more will come, so don't make the user wait the
 * full stall timeout.
 */
export const RECONNECT_GRACE_MS = 20000;

/** While the user's previous turn still runs server-side (409), retry the send this often. */
export const BUSY_RETRY_MS = 3000;

export const AssistantCopy = {
    TITLE: 'TherapyLake Assistant',
    SUBTITLE: 'Ask anything about TherapyLake',
    PLACEHOLDER: 'Ask a question…',
    DISCLAIMER: 'AI can make mistakes. Check important information.',
    STALLED: 'The assistant took too long to answer. Please try again — if it keeps happening, contact your administrator.',
    FAILED: 'The message could not be sent. Please try again — if it keeps happening, contact your administrator.',
    DISABLED: 'AI features are turned off for your organization. Contact your administrator.',
    /** Fallbacks if a governance 403/429/503 (or a not-configured turn failure) arrives without a message. */
    // Fallbacks only — the server's message wins. Platform switch off = a temporary outage.
    AI_DISABLED: 'AI is temporarily unavailable. Please try again later.',
    ASSISTANT_DISABLED: 'The AI assistant is temporarily unavailable. Please try again later.',
    LIMIT_REACHED: "You've used your AI limit for the assistant. Contact your administrator if you need more.",
    NOT_CONFIGURED: 'AI is not set up yet. Contact your administrator.',
    TOO_MANY_REQUESTS: 'Too many requests. Please wait a moment and try again.',
    BUSY: 'Still working on your previous message — one moment.',
    QUOTA_USED_UP: 'Limit reached — contact your administrator',
    CONNECTION_LOST: 'The connection dropped while the answer was coming in, so part of it may be missing. Please try again.',
    SESSION_EXPIRED: 'This chat was idle for a while, so the assistant no longer remembers the messages above. It starts fresh from here.',
    WELCOME_INTRO: 'I help you use TherapyLake. Ask me in your own words, for example:',
    WELCOME_POINTS: [
        'How to do something, step by step',
        'Where to find a page, tab or setting',
        'What a status, warning or error means',
    ],
    WELCOME_NOTE: "I answer from TherapyLake's help center and only about what your role can access. I can't see or change your clients', staff or billing records.",
};

/** Confirmation before "New chat": the current chat is discarded and can't be reopened. */
export const NewChatCopy = {
    TITLE: 'Start a new chat?',
    TEXT: "This chat will be cleared and can't be opened again.",
    CONFIRM: 'Start New Chat',
};

/** Label of the activity chip under a running reply. */
export const ActivityCopy = {
    [TurnActivity.RECONNECTING]: 'Connection lost — reconnecting…',
    [TurnActivity.WAITING]: 'Finishing your previous question first…',
};
export const TOOL_ACTIVITY_COPY = 'Looking that up…';

